import {
  IndexTable,
  LegacyCard,
  IndexFilters,
  useSetIndexFiltersMode,
  useIndexResourceState,
  Text,
  Icon,
  IndexFiltersMode,
  useBreakpoints,
  EmptySearchResult,
  Button,
} from "@shopify/polaris";
import { useState, useCallback, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { DeleteIcon } from "@shopify/polaris-icons";

export default function Customers() {
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const [orders, setOrders] = useState([]);
  const [itemStrings, setItemStrings] = useState([]);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Filtering);
  const [queryValue, setQueryValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [deleteuser, setDeleteUser] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [repstatus, setRepStatus] = useState("");
  const [statusid, setStatusid] = useState(null);

  const onCreateNewView = async (value) => {
    await sleep(500);
    setItemStrings([...itemStrings, value]);
    return true;
  };

  const onHandleCancel = () => {};

  const tabs = itemStrings.map((item, index) => ({
    content: item,
    index,
    onAction: () => {},
    id: `${item}-${index}`,
    isLocked: index === 0,
    actions: index === 0 ? [] : [],
  }));


  const handleQueryValueChange = useCallback((value) => {
    setQueryValue(value);
  }, []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(""), []);

  const handleFiltersClearAll = useCallback(() => {
    handleQueryValueRemove();
  }, [handleQueryValueRemove]);

  const filters = [];
  const appliedFilters = [];

  function debounce(func, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  }

  useEffect(() => {
    const fetchCustomers = async () => {
      const baseUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem("token");
      if (!token) return;

      setIsLoading(true); 

      try {
        let url = "";
        let method = "GET";
        const options = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };

        // DELETE user
        if (deleteuser) {
          url = `${baseUrl}/api/auth/users/${deleteuser}`;
          method = "DELETE";
        }

        // PUT - Update status
        else if (repstatus && statusid) {
          url = `${baseUrl}/api/auth/users/${statusid}`;
          method = "PUT";
          options.body = JSON.stringify({ status: repstatus });
        }

        // GET users
        else {
          url = queryValue
            ? `${baseUrl}/api/auth/users?search=${encodeURIComponent(
                queryValue
              )}&page=${page}&limit=10`
            : `${baseUrl}/api/auth/users?page=${page}&limit=10`;
          method = "GET";
        }

        options.method = method;

        const response = await fetch(url, options);

        if (!response.ok) {
          throw new Error("Failed to fetch users");
        }

        const data = await response.json();

        if (deleteuser) setDeleteUser("");
        if (repstatus) setRepStatus("");

        if (data.users && Array.isArray(data.users)) {
          setOrders(data.users);
          setTotalPages(data.totalPages);
        } else {
          setOrders([]);
        }
      } catch (error) {
        console.error("Error fetching users:", error.message);
      } finally {
        setIsLoading(false);
      }
    };

    const debouncedFetch = debounce(fetchCustomers, 400);
    debouncedFetch();
  }, [queryValue, page, deleteuser, repstatus, statusid]);

  const resourceName = {
    singular: "order",
    plural: "orders",
  };

  const userDelete = (id) => {
    setDeleteUser(id);
  };

  const handleToggle = async (index, id) => {
    const updatedUsers = [...orders];
    const currentStatus = updatedUsers[index].status;
    const newStatus = currentStatus === "active" ? "inactive" : "active";

    // UI update
    updatedUsers[index].status = newStatus;
    setOrders(updatedUsers);
    setRepStatus(newStatus);
    setStatusid(id);
  };

  const emptyStateMarkup = (
    <EmptySearchResult
      title={'No Sales Rep found'}
      description={'Try changing the filters or search term'}
      withIllustration
    />
  );

  const { selectedResources } = useIndexResourceState(orders);

  const rowMarkup =
    orders && orders.length > 0 ? (
      orders.map(({ name, email, country, phone, _id, status, orderCount, customId }, index) => (
        <IndexTable.Row
          id={_id}
          key={_id}
          selected={selectedResources.includes(_id)}
          position={index}
        >
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold" as="span">
              {name}
            </Text>
          </IndexTable.Cell>
          <IndexTable.Cell>{email}</IndexTable.Cell>
          <IndexTable.Cell>{country}</IndexTable.Cell>
          <IndexTable.Cell>{phone}</IndexTable.Cell>
          <IndexTable.Cell>
            <NavLink to={`/dashboard/orders?status=${customId}`} >{orderCount} Orders</NavLink>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <label className="switch_options" key={_id}>
              <input
                type="checkbox"
                checked={status === "active"}
                onChange={() => handleToggle(index, _id)}
              />
              <span className="slider"></span>
            </label>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <span
              onClick={() => userDelete(_id)}
              className="res_delete_icons"
              style={{ cursor: "pointer" }}
            >
              <Icon source={DeleteIcon} tone="base" />
            </span>
          </IndexTable.Cell>
        </IndexTable.Row>
      ))
    ) : (
      <IndexTable.Row>
        <IndexTable.Cell colSpan={3}>No Sales Rep found</IndexTable.Cell>
      </IndexTable.Row>
    );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <>
      <div className="button_option">
        <h2>All Sales Reps</h2>
        <NavLink to="add">
          <Button variant="primary">Add Sales Rep</Button>
        </NavLink>
      </div>

      <LegacyCard>
        <IndexFilters
          loading={isLoading}
          queryValue={queryValue}
          queryPlaceholder="Search customers"
          onQueryChange={handleQueryValueChange}
          onQueryClear={handleQueryValueRemove}
          cancelAction={{
            onAction: onHandleCancel,
            disabled: false,
            loading: false,
          }}
          tabs={tabs}
          canCreateNewView
          onCreateNewView={onCreateNewView}
          filters={filters}
          appliedFilters={appliedFilters}
          onClearAll={handleFiltersClearAll}
          mode={mode}
          setMode={setMode}
        />

        <IndexTable
          condensed={useBreakpoints().smDown}
          resourceName={resourceName}
          emptyState={emptyStateMarkup}
          itemCount={orders.length}
          headings={[
            { title: "Name" },
            { title: "Email" },
            { title: "Location" },
            { title: "Mobile Number" },
            { title: "Orders" },
            { title: "Enable/Disable" },
            { title: "Action" },
          ]}
          selectable={false}
          pagination={{
            hasNext: page < totalPages,
            onNext: () => handlePageChange(page + 1),
            hasPrevious: page > 1,
            onPrevious: () => handlePageChange(page - 1),
            currentPage: page,
            totalPages,
          }}
        >
          {rowMarkup}
        </IndexTable>
      </LegacyCard>
    </>
  );
}